import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FILTER_OPERATORS } from '../../utils/cubeQuery';
import { FieldIcon, IconSearch, IconClose } from './icons';

const TEXT_OPERATORS = ['equals', 'notEquals', 'contains', 'notContains', 'set', 'notSet'];
const NUMBER_OPERATORS = ['equals', 'notEquals', 'gt', 'gte', 'lt', 'lte', 'set', 'notSet'];

const isNumeric = (field) => field?.kind === 'measure' || field?.type === 'number';

export const operatorsFor = (field) => {
  const allowed = isNumeric(field) ? NUMBER_OPERATORS : TEXT_OPERATORS;
  return FILTER_OPERATORS.filter((o) => allowed.includes(o.value));
};

export const operatorLabel = (op) => FILTER_OPERATORS.find((o) => o.value === op)?.label || op;

/** One-line summary of a filter: "Region is North, South". */
export function describeFilter(filter, field) {
  const label = field?.label || filter.member.split('.').pop();
  const op = FILTER_OPERATORS.find((o) => o.value === filter.operator);
  if (!op) return label;
  if (!op.needsValue) return `${label} ${op.label}`;
  const values = Array.isArray(filter.values) ? filter.values : String(filter.values || '').split(',').map((v) => v.trim()).filter(Boolean);
  if (values.length === 0) return `${label} ${op.label} …`;
  const shown = values.length > 3 ? `${values.slice(0, 3).join(', ')} +${values.length - 3}` : values.join(', ');
  return `${label} ${op.label} ${shown}`;
}

/** Tokens typed by the user (Enter or comma adds one). */
const TokenInput = ({ values, onChange, placeholder, label }) => {
  const [draft, setDraft] = useState('');
  const commit = () => {
    const v = draft.trim();
    if (v && !values.includes(v)) onChange([...values, v]);
    setDraft('');
  };
  return (
    <div className="rf-tokens">
      {values.map((v) => (
        <span key={v} className="rf-token">
          {v}
          <button type="button" aria-label={`Remove ${v}`} onClick={() => onChange(values.filter((x) => x !== v))}><IconClose size={11} /></button>
        </span>
      ))}
      <input
        aria-label={label}
        value={draft}
        placeholder={values.length ? '' : placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); commit(); }
          if (e.key === 'Backspace' && !draft && values.length) onChange(values.slice(0, -1));
        }}
        onBlur={commit}
      />
    </div>
  );
};

/** Checklist of the field's distinct values, loaded from the data, with search. */
const ValueChecklist = ({ member, values, onChange, loadValues }) => {
  const [state, setState] = useState({ loading: true, error: null, options: [] });
  const [search, setSearch] = useState('');
  useEffect(() => {
    let live = true;
    setState({ loading: true, error: null, options: [] });
    loadValues(member)
      .then((options) => live && setState({ loading: false, error: null, options }))
      .catch((err) => live && setState({ loading: false, error: err?.message || 'Could not load values', options: [] }));
    return () => { live = false; };
  }, [member, loadValues]);

  const term = search.trim().toLowerCase();
  const options = useMemo(() => {
    // Loaded values keep their order; typed values not in the data follow.
    const all = [...state.options, ...values.filter((v) => !state.options.includes(v))];
    return term ? all.filter((o) => o.toLowerCase().includes(term)) : all;
  }, [state.options, values, term]);
  const toggle = (v) => onChange(values.includes(v) ? values.filter((x) => x !== v) : [...values, v]);
  const canAdd = term && !options.some((o) => o.toLowerCase() === term);

  return (
    <div className="rf-checklist">
      <div className="rf-search rf-search--small">
        <IconSearch size={13} />
        <input
          aria-label="Search values"
          placeholder="Search or type a value"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              const v = search.trim();
              if (v && !values.includes(v)) onChange([...values, v]);
              setSearch('');
            }
          }}
        />
      </div>
      <div className="rf-checklist-list" role="group" aria-label="Values">
        {state.loading && <div className="rf-muted">Loading values…</div>}
        {state.error && <div className="rf-muted">{state.error}. Type a value and press Enter.</div>}
        {canAdd && (
          <button type="button" className="rf-checklist-add" onClick={() => { onChange([...values, search.trim()]); setSearch(''); }}>
            Add “{search.trim()}”
          </button>
        )}
        {options.map((o) => (
          <label key={o} className="rf-check">
            <input type="checkbox" checked={values.includes(o)} onChange={() => toggle(o)} />
            <span>{o === '' ? '(blank)' : o}</span>
          </label>
        ))}
        {!state.loading && !state.error && options.length === 0 && !canAdd && <div className="rf-muted">No values.</div>}
      </div>
      {values.length > 0 && (
        <div className="rf-checklist-foot">
          <span>{values.length} selected</span>
          <button type="button" className="rf-link-btn" onClick={() => onChange([])}>Clear</button>
        </div>
      )}
    </div>
  );
};

/** Inline editor for one filter. */
const FilterEditor = ({ filter, field, onChange, onRemove, onDone, loadValues }) => {
  const ops = operatorsFor(field);
  const op = FILTER_OPERATORS.find((o) => o.value === filter.operator) || ops[0];
  const values = Array.isArray(filter.values) ? filter.values : [];
  const firstRef = useRef(null);
  useEffect(() => { firstRef.current?.focus(); }, []);
  const numeric = isNumeric(field);
  const listOperator = !numeric && (op.value === 'equals' || op.value === 'notEquals');

  return (
    <div className="rf-filter-editor" role="group" aria-label={`Filter on ${field?.label || filter.member}`}>
      <div className="rf-filter-editor-head">
        {field && <FieldIcon field={field} size={14} />}
        <strong>{field?.label || filter.member}</strong>
      </div>
      <select ref={firstRef} aria-label="Condition" value={op.value} onChange={(e) => onChange({ ...filter, operator: e.target.value })}>
        {ops.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {op.needsValue && listOperator && (
        <ValueChecklist member={filter.member} values={values} onChange={(v) => onChange({ ...filter, values: v })} loadValues={loadValues} />
      )}
      {op.needsValue && !listOperator && !numeric && (
        <TokenInput label="Values" values={values} placeholder="Type text and press Enter" onChange={(v) => onChange({ ...filter, values: v })} />
      )}
      {op.needsValue && numeric && (
        <input
          type="number"
          step="any"
          aria-label="Value"
          value={values[0] ?? ''}
          onChange={(e) => onChange({ ...filter, values: e.target.value === '' ? [] : [e.target.value] })}
        />
      )}
      <div className="rf-filter-editor-actions">
        <button type="button" className="rf-link-btn rf-link-btn--danger" onClick={onRemove}>Remove filter</button>
        <button type="button" className="rf-btn rf-btn--small rf-btn--primary" onClick={onDone}>Done</button>
      </div>
    </div>
  );
};

export default FilterEditor;
